(ns arthur.domain.pick
  "What is under the pointer on the stage, and which row a click on it selects.

  THE OPS ALREADY SAY. The stage draws a flat list of ops, and each op's `:node`
  is the row path of what drew it — the instances down to it and its own id — so
  hit-testing is a walk of the list, topmost first, and needs nothing resolved.

  WHICH LEVEL a click selects is Figma's and Illustrator's rule, and Flash's
  without its edit mode: a click selects the thing in the open symbol, a
  double-click goes one level into what is selected, ⌥-click goes straight to the
  shape itself. A click inside what is selected keeps it, so a deep selection can
  be dragged; one elsewhere selects at the same depth, beside it."
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.clip :as clip]
            [arthur.domain.node :as node]
            [arthur.domain.palette :as pal]))

(def ^:private slop
  "Stage pixels a click may miss by. The shapes here are a few pixels across."
  2)

(defn- path-of [op]
  (let [n (:node op)] (if (vector? n) n [n])))

(defn- near-segment? [x y ax ay bx by]
  (let [dx (- bx ax) dy (- by ay)
        l2 (+ (* dx dx) (* dy dy))
        t  (if (zero? l2) 0 (-> (/ (+ (* (- x ax) dx) (* (- y ay) dy)) l2) (max 0) (min 1)))
        ex (- x (+ ax (* t dx)))
        ey (- y (+ ay (* t dy)))]
    (<= (+ (* ex ex) (* ey ey)) (* slop slop))))

(defn- on-poly? [^js pts n x y]
  (let [px #(aget pts (* 2 (mod % n)))
        py #(aget pts (inc (* 2 (mod % n))))]
    (or (odd? (count (filter (fn [i]
                               (let [ay (py i) by (py (inc i))]
                                 (and (not= (> ay y) (> by y))
                                      (< x (+ (px i) (/ (* (- y ay) (- (px (inc i)) (px i)))
                                                        (- by ay)))))))
                             (range n))))
        (some #(near-segment? x y (px %) (py %) (px (inc %)) (py (inc %))) (range n)))))

(defn- trace-corners
  "A trace op's image rectangle on the stage, as four [x y] corners."
  [{[w h] :size m :m}]
  (for [[x y] [[0 0] [w 0] [w h] [0 h]]]
    [(+ (* (aget m 0) x) (* (aget m 2) y) (aget m 4))
     (+ (* (aget m 1) x) (* (aget m 3) y) (aget m 5))]))

(defn- on? [{:keys [kind pts n cx cy r size m]} x y]
  (case kind
    :poly (on-poly? pts n x y)
    :disc (<= (js/Math.hypot (- x cx) (- y cy)) (+ r slop))
    :rect (let [h (+ slop (/ size 2))]
            (and (<= (js/Math.abs (- x cx)) h) (<= (js/Math.abs (- y cy)) h)))
    ;; Into the image's own pixels, where the test is a rectangle however the
    ;; layer is turned or scaled.
    :trace (when-let [inv (node/invert m)]
             (let [[w h] size
                   u (+ (* (aget inv 0) x) (* (aget inv 2) y) (aget inv 4))
                   v (+ (* (aget inv 1) x) (* (aget inv 3) y) (aget inv 5))]
               (and (<= 0 u) (< u w) (<= 0 v) (< v h))))
    false))

;; A HOLE AND A LIGHT HAVE BOUNDS LIKE ANYTHING ELSE. A knockout and a remap
;; were both left out of this, which is the rule `hit-op` plays by — a click
;; goes through them to what shows under — carried over to a gesture it is wrong
;; for: a marquee asks what is INSIDE it, not what a point lands on, and leaving
;; them out meant the only way to select one anywhere was its timeline row.
(defn- op-bounds [{:keys [kind pts n cx cy r size] :as op}]
  (case kind
    :trace (let [cs (trace-corners op)]
             [(apply min (map first cs)) (apply min (map second cs))
              (apply max (map first cs)) (apply max (map second cs))])
    :poly (reduce (fn [b i]
                    (let [x (aget pts (* 2 i)) y (aget pts (inc (* 2 i)))]
                      (if b (let [[x0 y0 x1 y1] b]
                              [(min x0 x) (min y0 y) (max x1 x) (max y1 y)])
                          [x y x y]))) nil (range n))
    :disc [(- cx r) (- cy r) (+ cx r) (+ cy r)]
    :rect (let [h (/ size 2)] [(- cx h) (- cy h) (+ cx h) (+ cy h)])
    nil))

(defn in-rect
  "Distinct row paths whose drawn bounds intersect `[x0 y0 x1 y1]`. `depth`
  chooses objects at one hierarchy level, just as an ordinary stage click does."
  [ops [ax ay bx by] depth]
  (let [[rx0 rx1] [(min ax bx) (max ax bx)]
        [ry0 ry1] [(min ay by) (max ay by)]]
    (->> ops
         (keep (fn [op]
                 (when-let [[x0 y0 x1 y1] (op-bounds op)]
                   (when (and (<= x0 rx1) (<= rx0 x1) (<= y0 ry1) (<= ry0 y1))
                     (let [path (path-of op)]
                       (subvec path 0 (min (count path) (max 1 depth))))))))
         distinct vec)))

(defn- prefix? [a b]
  (and (<= (count a) (count b)) (= a (subvec b 0 (count a)))))

(defn hit-op
  "The topmost op in `ops`, in draw order, that SHOWS at stage point `[x y]`, or
  nil. A knockout is never hit: it is a hole, and a click in it is a click on
  whatever shows through — so it hides the ops beneath it in its own symbol, of
  the colour it clears. A remap is the same kind of thing the other way up: it is
  light on what is under it, so a click goes through it to what it lights.

  EXCEPT WHAT IS ALREADY SELECTED, which `selected` is the row path of. Both of
  those rules are about reaching PAST a shape, and neither has anything to say
  about the shape you have in your hands: a selected remap or knockout answered
  no point on the stage at all, so there was nothing to drag it by — the stage's
  move gesture is this hit-test on the ops, and `handles` draws a box round
  bounds that nothing could then grab. The first press on one started a marquee
  instead, which selected nothing and so threw the selection away, and that is
  the whole of why a palette swapper could be selected from a timeline row and
  then neither moved nor resized. The ordinary depth rule — a click inside what
  is selected keeps it, so a deep selection can be dragged — is `choose`'s, and
  this is the same rule reaching one step further down."
  ([ops point] (hit-op ops point nil))
  ([ops [x y] selected]
   (:op (reduce (fn [holes op]
                  (cond
                    (not (on? op x y)) holes
                    (and selected (prefix? selected (path-of op))) (reduced {:op op})
                    (:lut op) holes
                    (:knock op) (conj holes [(pop (path-of op)) (:knock op)])
                    (some (fn [[in k]] (and (prefix? in (path-of op))
                                            (or (neg? k) (== k (:color op)))))
                          holes) holes
                    :else (reduced {:op op})))
                [] (let [{traces true drawn false} (group-by #(= :trace (:kind %)) (rseq (vec ops)))]
                     (concat drawn traces))))))

(defn hit
  "The row path of the topmost op that shows at stage point `[x y]`, or nil.
  `selected` is what is selected now, which a light or a hole does not hide."
  ([ops point] (hit ops point nil))
  ([ops point selected] (some-> (hit-op ops point selected) path-of)))

(defn choose
  "The row path a click on `hit` selects, with `selected` the one selected now."
  [selected hit deep?]
  (cond
    (nil? hit) nil
    deep? hit
    (and selected (prefix? selected hit)) selected
    (and selected (prefix? (pop selected) hit)) (subvec hit 0 (count selected))
    :else [(first hit)]))

(defn deeper
  "One level into `selected` towards `hit`, for a double-click."
  [selected hit]
  (if (and selected hit (prefix? selected hit) (< (count selected) (count hit)))
    (subvec hit 0 (inc (count selected)))
    selected))

(defn bounds-of
  "A closure from a frame to `[x0 y0 x1 y1]` around what node `n` draws on it, in
  its own coordinates — nil on a frame it draws nothing on.

  A CLOSURE, as `clip/resolver` is, and for the same reason it is: inside an
  instance is its whole symbol resolved at that frame, and a resolver costs the
  symbol to BUILD and a lookup to RUN. Asking frame by frame through a fresh one
  is a resolver per frame.

  WHAT A SELECTION BOX IS DRAWN FROM, and what a pivot DEFAULTS to: a node
  nobody has pivoted turns about the middle of these bounds, and the first turn
  or scale writes that point down as its `[:xform :pivot]` — `gesture/pivot` and
  `gesture/with-pivot`. So the box and the cross start out as one computation,
  and ⌖ in the inspector brings a chosen pivot back to it (`gesture/centred`).

  A DEFAULT, AND NOT WHERE THE PIVOT LIVES. Once a pivot is the node's own, this
  is not consulted for it again: the pivot is a choice, and a choice that
  silently followed the drawing would mean adding a shape to a symbol re-aimed
  every keyed spin of every instance of it. See `domain/gesture`."
  [document store sid n]
  (let [grow (fn [[x0 y0 x1 y1 :as b] x y]
               (if b [(min x0 x) (min y0 y) (max x1 x) (max y1 y)] [x y x y]))
        at   (fn [f p] (ch/value-at (get (node/channels n) p) f store))]
    (case (if (some->> (node/source n) (clip/symbol document) clip/trace?) :trace (:kind n))
      ;; A tracing symbol draws nothing to resolve: what it covers is its media's
      ;; pixels, in its own coordinates, on every frame it shows one.
      :trace
      (let [{:keys [width height]} (clip/symbol document (node/source n))]
        (fn [f] (when (clip/placed-frame document n f) [0 0 width height])))

      :instance
      ;; ONE RESOLVER PER DRAWING THE LANE CAN SHOW, built once for the reason
      ;; the single one used to be: a resolver costs the symbol to build and a
      ;; lookup to run, and a lane asked frame by frame through a fresh one is a
      ;; resolver per frame.
      (let [loop?     (get-in n [:time :loop?])
            resolvers (into {} (map (fn [child]
                                      [child (clip/resolver document child store pal/index-of {})]))
                            (node/sources n))]
        (fn [f0]
          (let [shown  (clip/placed-frame document n f0)
                frames (when shown (clip/frames document (:symbol shown)))
                f      (when (number? frames)
                         (if loop? (mod (:frame shown) frames) (:frame shown)))
                resolve (when shown (get resolvers (:symbol shown)))]
            (when (and resolve (< -1 f frames))
              (reduce (fn [b {:keys [kind pts n cx cy r size]}]
                        (case kind
                          :poly (reduce #(grow %1 (aget pts (* 2 %2)) (aget pts (inc (* 2 %2))))
                                        b (range n))
                          :disc (-> b (grow (- cx r) (- cy r)) (grow (+ cx r) (+ cy r)))
                          :rect (let [h (/ size 2)]
                                  (-> b (grow (- cx h) (- cy h)) (grow (+ cx h) (+ cy h))))
                          b))
                      nil
                      (resolve f))))))
      :poly (fn [f]
              (let [pts (at f [:geom :pts])]
                (when-not (ch/nothing? pts)
                  (reduce (fn [b i] (grow b (ch/component pts (* 2 i)) (ch/component pts (inc (* 2 i)))))
                          nil (range (quot (if (vector? pts) (count pts) (.-length pts)) 2))))))
      :disc (fn [f]
              (let [r (at f [:geom :radius])]
                (when-not (ch/nothing? r) [(- r) (- r) r r])))
      :rect (fn [f]
              (let [s (at f [:geom :size])]
                (when-not (ch/nothing? s) (let [h (/ s 2)] [(- h) (- h) h h]))))
      (constantly nil))))
